import { useEffect, useState, useCallback } from "react";
import UpdatePanel from "@/components/UpdatePanel";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Play, RefreshCcw, Loader2, Download, PlayCircle, Search } from "lucide-react";
import ToolLogo from "@/components/ToolLogo";
import StopScraperButton from "@/components/StopScraperButton";
import ScrapeModeSelector from "@/components/ScrapeModeSelector";

function formatBytes(n) {
  if (!n && n !== 0) return "—";
  const units = ["B", "kB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}

// Unlike the other 3 paginated providers, this source is a Laravel CURSOR
// chain (~3,406 sequential pages) rather than seekable page numbers, so
// there's no "missing pages" list — only linear progress. A ~3,406-entry
// chip list would be unusable, hence a percentage bar instead (see
// GET /api/dynoecu/scrape/progress).
export default function DynoecuScrapeControl() {
  const [status, setStatus] = useState(null);
  const [progress, setProgress] = useState(null);
  const [starting, setStarting] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [syncingIndex, setSyncingIndex] = useState(false);
  const [useFlaresolverr, setUseFlaresolverr] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: st }, { data: prog }] = await Promise.all([
        api.get("/dynoecu/scrape/status"),
        api.get("/dynoecu/scrape/progress"),
      ]);
      setStatus(st);
      setProgress(prog);
    } catch (err) {
      console.warn("status error", err);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  const start = async () => {
    setStarting(true);
    try {
      await api.post("/dynoecu/scrape/run", null, {
        params: { reset: false, use_flaresolverr: useFlaresolverr },
      });
      toast.success("Original-ECU-Dateien-Scraper gestartet (ohne Daten zu löschen)");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Konnte Scraper nicht starten";
      toast.error(msg);
    } finally {
      setStarting(false);
    }
  };

  const resume = async () => {
    setResuming(true);
    try {
      await api.post("/dynoecu/scrape/run", null, {
        params: { only_missing: true, use_flaresolverr: useFlaresolverr },
      });
      toast.success("Setzt beim letzten gespeicherten Cursor fort…");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Konnte Lauf nicht fortsetzen";
      toast.error(msg);
    } finally {
      setResuming(false);
    }
  };

  const syncSearchIndex = async () => {
    setSyncingIndex(true);
    try {
      const { data } = await api.post("/dynoecu/search-index/sync");
      toast.success(`Such-Index aktualisiert (${data.synced.toLocaleString("de-DE")} Einträge)`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Konnte Such-Index nicht aktualisieren";
      toast.error(msg);
    } finally {
      setSyncingIndex(false);
    }
  };

  const pagesDone = progress?.pages_done ?? 0;
  const totalPages = progress?.total_expected_pages ?? 0;
  const pct = totalPages ? Math.min(100, (pagesDone / totalPages) * 100) : 0;

  return (
    <div data-testid="dynoecu-scrape-page" className="space-y-8">
      <div>
        <div className="flex items-center gap-2 mb-3">
          <ToolLogo source="dynoecu" />
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D32222]">
            Original ECU Dateien · Scrape Control
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Control card */}
        <div className="rounded-xl border border-neutral-800 bg-panel p-6 space-y-5">
          <div className="text-sm uppercase tracking-widest text-neutral-500">
            Daten prüfen
          </div>
          <ScrapeModeSelector direct={useFlaresolverr} onChange={setUseFlaresolverr} disabled={status?.running} testId="dynoecu-scrape-mode-selector" />
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              data-testid="dynoecu-start-scrape-btn"
              onClick={start}
              disabled={starting || status?.running}
              className="bg-[#D32222] hover:bg-[#E13636] text-white font-medium"
            >
              {starting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Prüfen / aktualisieren
            </Button>
            <StopScraperButton endpoint="/dynoecu/scrape/stop" running={status?.running} onStopped={load} testId="dynoecu-stop-scrape-btn" />
            <Button
              data-testid="dynoecu-resume-btn"
              onClick={resume}
              disabled={resuming || status?.running || progress?.completed}
              variant="outline"
              className="border-neutral-700 bg-neutral-900 hover:bg-neutral-800 text-neutral-100"
              title={
                progress?.completed
                  ? "Bereits vollständig durchlaufen"
                  : "Setzt beim letzten gespeicherten Cursor fort, statt bei Seite 1 neu zu starten"
              }
            >
              {resuming ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <PlayCircle className="w-4 h-4 mr-2" />
              )}
              Fortsetzen
            </Button>
            <Button
              data-testid="dynoecu-refresh-status-btn"
              variant="outline"
              className="border-neutral-700 bg-neutral-900 hover:bg-neutral-800 text-neutral-100"
              onClick={load}
            >
              <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button
              data-testid="dynoecu-sync-index-btn"
              onClick={syncSearchIndex}
              disabled={syncingIndex}
              variant="outline"
              title="Überträgt den aktuellen Datenstand in den Meilisearch-Index für die Sofortsuche"
              className="border-neutral-700 bg-neutral-900 hover:bg-neutral-800 text-neutral-100"
            >
              {syncingIndex ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              Such-Index aktualisieren
            </Button>
            <a
              href={`${api.defaults.baseURL}/dynoecu/scrape/output`}
              data-testid="dynoecu-download-json-link"
              className="ml-auto inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-[#E13636] transition"
            >
              <Download className="w-4 h-4" /> dynoecu_output.json
            </a>
          </div>
        </div>

        {/* Status card */}
        <div className="rounded-xl border border-neutral-800 bg-panel p-6">
          <div className="text-sm uppercase tracking-widest text-neutral-500 mb-4">
            Status
          </div>
          <div className="grid grid-cols-2 gap-4">
            <StatusPill
              label="Zustand"
              value={
                status?.running ? (
                  <span className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                    aktiv (pid {status.pid})
                  </span>
                ) : (
                  <span className="text-neutral-400">idle</span>
                )
              }
              testid="dynoecu-status-running"
            />
            <StatusPill
              label="Dateien in DB"
              value={
                <span
                  className="tabular-nums text-[#E13636]"
                  data-testid="dynoecu-status-db-count"
                >
                  {status?.items_in_db?.toLocaleString("de-DE") ?? "—"}
                </span>
              }
            />
            <StatusPill
              label="dynoecu_output.json"
              value={formatBytes(status?.output_json_size)}
              testid="dynoecu-status-output-size"
            />
            <StatusPill
              label="Letzter Fortschritt"
              value={
                <span className="text-[11px] font-mono text-neutral-400 line-clamp-1">
                  {status?.last_progress || "—"}
                </span>
              }
              testid="dynoecu-status-last-progress"
            />
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="rounded-xl border border-neutral-800 bg-panel p-6 space-y-4">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D32222] mb-2">
              Fortschritt
            </div>
            <p className="text-sm text-neutral-400 max-w-2xl">
              Die Quelle paginiert über eine fortlaufende Cursor-Kette, nicht
              über direkt ansteuerbare Seitenzahlen — es gibt daher keine
              Liste "fehlender Seiten", nur einen linearen Fortschritt seit
              dem letzten gespeicherten Cursor. "Fortsetzen" macht dort
              weiter, wo der letzte Lauf aufgehört hat.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <MiniStat
              label="Seiten"
              value={`${pagesDone.toLocaleString("de-DE")} / ${totalPages.toLocaleString("de-DE")}`}
              testid="dynoecu-progress-pages"
            />
            <MiniStat
              label="Dateien"
              value={progress?.rows_in_db ?? "—"}
              testid="dynoecu-progress-rows"
            />
            <MiniStat
              label="Nicht zugeordnet"
              value={progress?.unmatched_brand_count ?? 0}
              accent={progress?.unmatched_brand_count ? "text-amber-400" : "text-neutral-500"}
              testid="dynoecu-progress-unmatched"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <div className="h-2.5 rounded-full bg-neutral-900 border border-neutral-800 overflow-hidden">
            <div
              data-testid="dynoecu-progress-bar"
              className={`h-full rounded-full transition-all ${progress?.completed ? "bg-emerald-500" : "bg-[#D32222]"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="text-xs text-neutral-500 tabular-nums">
            {progress?.completed ? "Vollständig durchlaufen" : `${pct.toFixed(1)}%`}
          </div>
        </div>
      </div>

      <UpdatePanel provider="dynoecu" />

      {/* Log */}
      <div className="rounded-xl border border-neutral-800 bg-overlay overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-neutral-800">
          <div className="text-xs uppercase tracking-widest text-neutral-500">
            spider log · tail
          </div>
          <div className="text-[10px] text-neutral-600 font-mono">
            /tmp/dynoecu_scraper_full.log
          </div>
        </div>
        <pre
          data-testid="dynoecu-scrape-log"
          className="p-4 text-[11px] leading-relaxed text-neutral-300 font-mono max-h-[400px] overflow-auto whitespace-pre-wrap"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          {(status?.log_tail || []).join("\n") || "keine Logs bisher…"}
        </pre>
      </div>
    </div>
  );
}

function MiniStat({ label, value, accent = "text-neutral-100", testid }) {
  return (
    <div data-testid={testid} className="min-w-[80px]">
      <div
        className={`text-lg tabular-nums ${accent}`}
        style={{ fontFamily: "'Orbitron', sans-serif" }}
      >
        {typeof value === "number" ? value.toLocaleString("de-DE") : value}
      </div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500">
        {label}
      </div>
    </div>
  );
}

function StatusPill({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-lg border border-neutral-800 bg-overlay/40 p-3"
    >
      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
        {label}
      </div>
      <div className="mt-1 text-sm text-neutral-100">{value}</div>
    </div>
  );
}
